import * as React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { MenuIcon } from 'lucide-react';

import { TdcLogoPlaceholder } from '@/components/brand/tdc-logo-placeholder';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import { TDC_HOME_URL } from '@/lib/external-links';
import { primaryNavigation, utilityNavigation, type NavItem } from '@/lib/navigation';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/* Nav segmentation — groups consecutive items by `group` label       */
/* ------------------------------------------------------------------ */

type NavSegment =
  | { type: 'item'; item: NavItem }
  | { type: 'group'; label: string; items: NavItem[] };

function segmentNavigation(items: NavItem[]): NavSegment[] {
  const segments: NavSegment[] = [];

  for (const item of items) {
    if (!item.group) {
      segments.push({ type: 'item', item });
      continue;
    }

    const last = segments.at(-1);
    if (last?.type === 'group' && last.label === item.group) {
      last.items.push(item);
      continue;
    }

    segments.push({ type: 'group', label: item.group, items: [item] });
  }

  return segments;
}

/* ------------------------------------------------------------------ */
/* Desktop nav — flat list with vertical divider brackets              */
/* ------------------------------------------------------------------ */

const desktopNavLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'focus-visible:ring-ring relative rounded-md px-2.5 py-1.5 text-sm transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none xl:px-3',
    isActive
      ? 'text-foreground font-medium after:absolute after:inset-x-2.5 after:bottom-0.5 after:h-px after:bg-primary hover:text-[#FFD100] hover:after:bg-[#FFD100] xl:after:inset-x-3'
      : 'text-foreground/80 hover:bg-[#FFD100]/20 hover:text-[#FFD100]'
  );

function DesktopNav() {
  const segments = segmentNavigation(primaryNavigation);

  return (
    <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
      {segments.map((segment, segIndex) => (
        <React.Fragment key={segment.type === 'item' ? segment.item.title : segment.label}>
          {segIndex > 0 && (
            <span aria-hidden className="bg-border mx-0.5 h-5 w-px self-center" />
          )}

          {segment.type === 'item' ? (
            segment.item.to && (
              <NavLink
                to={segment.item.to}
                end={segment.item.to === '/'}
                className={desktopNavLinkClass}
              >
                {segment.item.title}
              </NavLink>
            )
          ) : (
            <div
              role="group"
              aria-label={segment.label}
              className="flex items-center gap-2"
            >
              <p className="text-muted-foreground/70 text-[9px] font-semibold leading-tight tracking-[0.14em] uppercase">
                {segment.label}
              </p>
              <div className="border-foreground/25 flex items-center gap-0 rounded-lg border-[1.5px] bg-gradient-to-b from-white to-[#EDEAE3] px-1 py-0.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-2px_3px_rgba(0,0,0,0.07),0_2px_4px_rgba(0,0,0,0.14),0_5px_10px_-3px_rgba(0,0,0,0.16)]">
                {segment.items.map((item) =>
                  item.to ? (
                    <NavLink key={item.title} to={item.to} className={desktopNavLinkClass}>
                      {item.title}
                    </NavLink>
                  ) : null
                )}
              </div>
            </div>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* Mobile nav — sheet with section headers                             */
/* ------------------------------------------------------------------ */

function MobileNav({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const links = [...primaryNavigation, ...utilityNavigation];
  const segments = segmentNavigation(links);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-foreground lg:hidden"
          aria-label="Open navigation menu"
        >
          <MenuIcon className="size-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="border-border bg-card w-full max-w-xs">
        <SheetHeader className="border-border border-b text-left">
          <SheetTitle className="font-display">Menu</SheetTitle>
        </SheetHeader>
        <nav aria-label="Mobile primary" className="flex flex-col px-2 py-4">
          {segments.map((segment, segIndex) => {
            if (segment.type === 'item') {
              if (!segment.item.to) return null;
              return (
                <React.Fragment key={segment.item.title}>
                  {segIndex > 0 && <div className="border-border my-1 h-px" />}
                  <NavLink
                    to={segment.item.to}
                    end={segment.item.to === '/'}
                    onClick={() => onOpenChange(false)}
                    className={({ isActive }) =>
                      cn(
                        'focus-visible:ring-ring flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
                        isActive
                          ? 'bg-secondary text-foreground font-medium'
                          : 'text-muted-foreground hover:bg-secondary/70 hover:text-foreground'
                      )
                    }
                  >
                    <segment.item.icon aria-hidden className="text-primary size-4 shrink-0" />
                    {segment.item.title}
                  </NavLink>
                </React.Fragment>
              );
            }

            // Group segment — bordered box + caption
            return (
              <div key={segment.label} role="group" aria-label={segment.label}>
                {segIndex > 0 && <div className="border-border my-1.5 h-px" />}
                <div className="border-foreground/25 mx-1 rounded-lg border-[1.5px] bg-gradient-to-b from-white to-[#EDEAE3] p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-2px_3px_rgba(0,0,0,0.07),0_2px_4px_rgba(0,0,0,0.14),0_5px_10px_-3px_rgba(0,0,0,0.16)]">
                  <p className="text-muted-foreground/70 px-1 pb-1 text-[9px] font-semibold tracking-[0.14em] uppercase">
                    {segment.label}
                  </p>
                  <div className="flex flex-col">
                    {segment.items.map((item) =>
                      item.to ? (
                        <NavLink
                          key={item.title}
                          to={item.to}
                          onClick={() => onOpenChange(false)}
                          className={({ isActive }) =>
                            cn(
                              'focus-visible:ring-ring flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
                              isActive
                                ? 'bg-secondary text-foreground font-medium'
                                : 'text-muted-foreground hover:bg-secondary/70 hover:text-foreground'
                            )
                          }
                        >
                          <item.icon aria-hidden className="text-primary size-4 shrink-0" />
                          {item.title}
                        </NavLink>
                      ) : null
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <Button asChild variant="cta" className="mt-4 w-full">
            <Link to="/donate" onClick={() => onOpenChange(false)}>
              Donate
            </Link>
          </Button>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/* Header                                                              */
/* ------------------------------------------------------------------ */

export function SiteHeader() {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  return (
    <header className="border-border/60 bg-background/90 supports-[backdrop-filter]:bg-background/75 sticky top-0 z-40 border-b backdrop-blur-md">
      <div className="flex h-16 w-full max-w-none items-center gap-3 overflow-visible px-4 sm:px-6 lg:h-20 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <a
            href={TDC_HOME_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="focus-visible:ring-ring shrink-0 rounded-md focus-visible:ring-2 focus-visible:outline-none"
          >
            <TdcLogoPlaceholder />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
          <div className="bg-border hidden h-7 w-px sm:block" aria-hidden />
          <Link
            to="/"
            className="focus-visible:ring-ring group min-w-0 rounded-md focus-visible:ring-2 focus-visible:outline-none"
          >
            <span className="font-display text-foreground block whitespace-nowrap text-sm font-bold tracking-[0.12em] sm:text-[0.95rem]">
              NextOnVera
            </span>
            <span className="text-muted-foreground hidden text-[11px] leading-none tracking-wide sm:block">
              Proudly supported by Tour de Cure
            </span>
          </Link>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <DesktopNav />
          <Button asChild variant="cta" className="hidden lg:inline-flex">
            <Link to="/donate">Donate</Link>
          </Button>
          <MobileNav open={mobileOpen} onOpenChange={setMobileOpen} />
        </div>
      </div>
    </header>
  );
}
